import React, { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { createCustomer, updateCustomer } from '../../../../slices/customersSlice';
import { setLoading } from '../../../../slices/feedbackSlice';

const useStyles = makeStyles()((theme) => ({

  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.secondary.dark,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}
));

export default function AddCustomerModal({ display, handleClose, customer = {} }) {
  const [customerProperties, setCustomerProperties] = useState({});
  const { classes } = useStyles();
  const { t } = useTranslation('customers');
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.feedback.loading);
  // Une fiche sans identifiant est une inscription ; avec, une correction.
  // C'est le seul écart entre les deux usages du formulaire.
  const isEditing = Boolean(customer && customer.id);

  // 🔑 Seuls le prénom et le nom sont obligatoires — c'est ce que dit
  // `Customer` dans le modèle, où `email`, `phone`, `language` et `note` sont
  // tous « blank=True, null=True ». Le formulaire en marquait trois d'une
  // étoile : l'API acceptait pourtant la fiche sans eux, et la documentation
  // dit d'ailleurs le contraire du formulaire. Une liste de membres réelle
  // comporte toujours des personnes sans courriel ni téléphone ; les exiger
  // pousse à inventer une adresse, et c'est cette adresse inventée qu'on
  // retrouve ensuite dans les rappels.
  useEffect(() => {
    setCustomerProperties(
      {
        firstName: {
          variant: 'outlined',
          required: true,
          fullWidth: true,
          id: 'first_name',
          label: t('firstName'),
          name: 'firstName',
          value: customer.first_name || '',
        },
        lastName: {
          variant: 'outlined',
          required: true,
          fullWidth: true,
          name: 'lastName',
          label: t('lastName'),
          id: 'last_name',
          value: customer.last_name || '',
        },
        email: {
          variant: 'outlined',
          required: false,
          fullWidth: true,
          name: 'email',
          label: t('email'),
          id: 'email',
          value: customer.email || '',
        },
        language: {
          variant: 'outlined',
          required: false,
          select: true,
          fullWidth: true,
          name: 'language',
          label: t('language'),
          id: 'language',
          value: customer.language || 'fr',
          children:
                        [
                          <MenuItem key="fr" value="fr">
                            {t('add_customer.fr')}
                          </MenuItem>,
                          <MenuItem key="en" value="en">
                            {t('add_customer.en')}
                          </MenuItem>,
                        ]

          ,
        },
        phoneNumber: {
          variant: 'outlined',
          required: false,
          fullWidth: true,
          name: 'phoneNumber',
          label: t('phoneNumber'),
          id: 'phone',
          value: customer.phone || '',
        },
        note: {
          name: 'note',
          variant: 'outlined',
          required: false,
          fullWidth: true,
          multiline: true,
          rows: 4,
          id: 'note',
          label: t('note'),
          value: customer.note || '',
        },
      },
    );
  }, [t, customer]);

  const handleSubmit = () => {
    dispatch(setLoading(true));
    const newCustomer = Object.values(customerProperties).reduce((acc, elem) => {
      acc[elem.id] = elem.value;
      return acc;
    }, {});
    const action = isEditing
      ? updateCustomer({ customerId: customer.id, customerData: newCustomer })
      : createCustomer(newCustomer);
    dispatch(action).unwrap()
      .then(() => {
        dispatch(setLoading(false));
        handleClose();
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  };

  const handleChange = (event) => {
    event.preventDefault();
    const { target } = event;
    let { value, name } = target;
    if (name === 'isbn') {
      value = value.replace(/-/g, '');
    }
    if (name) {
      setCustomerProperties({
        ...customerProperties,
        [name]: {
          ...customerProperties[name],
          value,
        },
      });
    }
  };

  const modalContent = (
    <>
      <DialogTitle className={classes.root}>
        <Typography type="h3" variant="h3">
          {isEditing ? t('edit_customer.title') : t('add_customer.title')}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>
      <DialogContent dividers>

        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          onChange={handleChange}
          onClick={handleChange}
        >
          <Grid container spacing={1}>

            {Object.values(customerProperties).map((property) => (
              <Grid item xs={12} key={property.id}>
                {/* Même piège que dans AddBookModal : sans onChange explicite, le choix
                    dans le menu de la liste n'arrive jamais à handleChange. */}
                <TextField {...property} onChange={property.select ? handleChange : undefined} />
              </Grid>
            ))}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <div className={classes.wrapper}>
          <Button
            type="submit"
            disabled={loading}
            fullWidth
            variant="contained"
            color="primary"

            onClick={handleSubmit}
          >
            {t('add_customer.save_button')}
          </Button>
          {loading && (
          <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>

      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={display}
      onClose={handleClose}
      fullWidth
    >
      {modalContent}
    </Dialog>
  );
}
